import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  Typography,
  Textarea,
  Button,
  Spinner,
  Checkbox,
} from '@material-tailwind/react';
import {
  CurrencyDollarIcon,
  ChartBarIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';

// Chart ID mapping (API ID -> content property name)
const CHART_CONFIG = [
  { id: 'inventory-chart', prop: 'inventoryTrend', label: 'Inventory Over Time' },
  { id: 'price-trend-chart', prop: 'priceAndSalesTrend', label: 'Listing vs Sale Price Trends' },
  { id: 'scatter-chart', prop: 'totalTimeVsPrice', label: 'Total Time vs Price' },
  { id: 'price-distribution-chart', prop: 'priceDistribution', label: 'Price Distribution' },
  { id: 'price-by-year-chart', prop: 'avgPriceByYear', label: 'Avg Price by Year' },
  { id: 'hours-by-year-chart', prop: 'avgHoursByYear', label: 'Avg Hours by Year' },
];

function EditableMetricCard({ icon: Icon, label, value, onChange, type = 'number', prefix, suffix, color = 'blue' }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600',
    gray: 'bg-gray-50 text-gray-600',
  };

  return (
    <div className="flex items-center gap-2 p-2 bg-white rounded-lg border">
      <div className={`p-1.5 rounded-lg ${colorClasses[color]}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1">
        <Typography variant="small" color="gray" className="text-xs mb-1">
          {label}
        </Typography>
        <div className="flex items-center gap-1">
          {prefix && <span className="text-xs text-gray-500">{prefix}</span>}
          <input
            type={type}
            value={value ?? ''}
            onChange={(e) => onChange(type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
            className="w-full text-sm font-bold border-b border-gray-300 focus:border-blue-500 focus:outline-none bg-transparent py-0.5"
          />
          {suffix && <span className="text-xs text-gray-500">{suffix}</span>}
        </div>
      </div>
    </div>
  );
}

const EXCLUSION_REASON_LABELS = {
  user_excluded: 'User excluded',
  missing_price: 'Missing price',
  missing_hours: 'Missing hours',
  outlier: 'Statistical outlier',
  duplicate: 'Duplicate',
};

function MarketTrendsSection({
  content,
  listingsSummary,
  hiddenCharts = [],
  onToggleChart,
  onTrendPointClick,
  onRestoreExclusion,
  isApplyingExclusions,
  onSave,
  onReset,
  isSaving
}) {
  // Editable metrics state (matches draft structure)
  const [editedLiveOnMarket, setEditedLiveOnMarket] = useState(0);
  const [editedAvgDaysOnMarket, setEditedAvgDaysOnMarket] = useState(0);
  const [editedAvgPriceChange, setEditedAvgPriceChange] = useState(0);
  const [editedCommentary, setEditedCommentary] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  // Reset all fields when content changes
  useEffect(() => {
    if (content) {
      setEditedLiveOnMarket(content.liveOnMarket ?? 0);
      setEditedAvgDaysOnMarket(content.avgDaysOnMarket ?? 0);
      setEditedAvgPriceChange(content.avgPriceChange ?? 0);
      setEditedCommentary(content.commentary ?? '');
      setHasChanges(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content?.liveOnMarket, content?.avgDaysOnMarket, content?.avgPriceChange, content?.commentary]);

  // Check for changes
  useEffect(() => {
    if (!content) return;
    const changed =
      editedLiveOnMarket !== (content.liveOnMarket ?? 0) ||
      editedAvgDaysOnMarket !== (content.avgDaysOnMarket ?? 0) ||
      editedAvgPriceChange !== (content.avgPriceChange ?? 0) ||
      editedCommentary !== (content.commentary ?? '');
    setHasChanges(changed);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedLiveOnMarket, editedAvgDaysOnMarket, editedAvgPriceChange, editedCommentary, content?.liveOnMarket, content?.avgDaysOnMarket, content?.avgPriceChange, content?.commentary]);

  const handleSave = () => {
    if (onSave) {
      onSave({
        liveOnMarket: editedLiveOnMarket,
        avgDaysOnMarket: editedAvgDaysOnMarket,
        avgPriceChange: editedAvgPriceChange,
        commentary: editedCommentary,
      });
    }
  };

  const handleReset = () => {
    setEditedLiveOnMarket(content?.liveOnMarket ?? 0);
    setEditedAvgDaysOnMarket(content?.avgDaysOnMarket ?? 0);
    setEditedAvgPriceChange(content?.avgPriceChange ?? 0);
    setEditedCommentary(content?.commentary ?? '');
    setHasChanges(false);
    if (onReset) {
      onReset();
    }
  };

  if (!content) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <Typography>No market trends data available</Typography>
      </div>
    );
  }

  const { charts, trends } = content;

  // Get latest median price from trends array (read-only display)
  const latestTrend = trends && trends.length > 0 ? trends[trends.length - 1] : null;
  const latestMedianPrice = latestTrend?.medianPrice;

  return (
    <div className="space-y-3 p-2 overflow-auto h-full">
      {/* Editable Key Metrics */}
      <Card className="border border-blue-200">
        <CardBody className="p-3">
          <div className="flex items-center justify-between mb-2">
            <Typography variant="small" className="font-medium text-xs text-blue-800">
              Current Market Highlights
            </Typography>
            {hasChanges && (
              <span className="text-xs text-amber-600 font-medium">Modified</span>
            )}
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            {/* Read-only: Latest median price from trends */}
            <div className="flex items-center gap-2 p-2 bg-white rounded-lg border">
              <div className="p-1.5 rounded-lg bg-blue-50 text-blue-600">
                <CurrencyDollarIcon className="h-4 w-4" />
              </div>
              <div>
                <Typography variant="small" color="gray" className="text-xs">
                  Median Price
                </Typography>
                <Typography variant="small" color="blue-gray" className="font-bold">
                  {latestMedianPrice ? `$${latestMedianPrice.toLocaleString()}` : 'N/A'}
                </Typography>
              </div>
            </div>
            {/* Editable: Live on market */}
            <EditableMetricCard
              icon={ChartBarIcon}
              label="Live on Market"
              value={editedLiveOnMarket}
              onChange={setEditedLiveOnMarket}
              color="green"
            />
            {/* Editable: Avg Days on Market */}
            <EditableMetricCard
              icon={ClockIcon}
              label="Avg Days on Market"
              value={editedAvgDaysOnMarket}
              onChange={setEditedAvgDaysOnMarket}
              suffix="days"
              color="amber"
            />
            {/* Editable: Avg Price Change */}
            <EditableMetricCard
              icon={ArrowTrendingUpIcon}
              label="Price Change"
              value={editedAvgPriceChange}
              onChange={setEditedAvgPriceChange}
              suffix="%"
              color={editedAvgPriceChange >= 0 ? 'green' : 'amber'}
            />
          </div>
        </CardBody>
      </Card>

      {/* Editable Market Analysis Commentary */}
      <Card className="border border-blue-200">
        <CardBody className="p-2">
          <Typography variant="small" className="font-medium text-xs mb-1 text-blue-800">
            Market Analysis
          </Typography>
          <Textarea
            value={editedCommentary}
            onChange={(e) => setEditedCommentary(e.target.value)}
            rows={5}
            className="mb-2 text-xs"
            label="Commentary"
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="outlined"
              size="sm"
              onClick={handleReset}
              disabled={!hasChanges || isSaving}
              className="py-1 px-2 text-xs"
            >
              Reset
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
              className="py-1 px-2 text-xs"
            >
              {isSaving ? (
                <>
                  <Spinner className="h-3 w-3 mr-1" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Chart Visibility Settings */}
      {onToggleChart && (
        <Card className="border border-gray-200">
          <CardBody className="p-3">
            <div className="flex items-center gap-2 mb-2">
              <EyeIcon className="h-4 w-4 text-gray-500" />
              <Typography variant="small" className="font-medium text-xs text-gray-700">
                Chart Visibility in Report
              </Typography>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
              {CHART_CONFIG.map((chartConfig) => {
                const isVisible = !hiddenCharts.includes(chartConfig.id);

                return (
                  <div
                    key={chartConfig.id}
                    className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                    onClick={() => onToggleChart(chartConfig.id)}
                  >
                    <Checkbox
                      checked={isVisible}
                      onChange={() => {}} // Handled by parent div click
                      className="h-4 w-4 pointer-events-none"
                      containerProps={{ className: 'p-0' }}
                    />
                    <Typography variant="small" className="text-xs">
                      {chartConfig.label}
                    </Typography>
                  </div>
                );
              })}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Charts Grid (Preview) */}
      {charts && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
          {CHART_CONFIG.map((chartConfig) => {
            const chartHtml = charts[chartConfig.prop];
            if (!chartHtml) return null;

            // Skip hidden charts in preview
            const isHidden = hiddenCharts.includes(chartConfig.id);
            if (isHidden) return null;

            const isScatterChart = chartConfig.id === 'scatter-chart';

            return (
              <Card key={chartConfig.id}>
                <CardBody className="p-2">
                  <Typography variant="small" className="font-medium text-xs mb-1">
                    {chartConfig.label}
                  </Typography>
                  <div
                    className={`w-full ${chartConfig.id === 'inventory-chart' || chartConfig.id === 'price-trend-chart' ? 'cursor-pointer' : ''}`}
                    onClick={() => {
                      if (chartConfig.id === 'inventory-chart') onTrendPointClick?.('inventory');
                      if (chartConfig.id === 'price-trend-chart') onTrendPointClick?.('price');
                    }}
                    dangerouslySetInnerHTML={{ __html: chartHtml }}
                  />
                  {/* Display excluded listings under scatter chart */}
                  {isScatterChart && listingsSummary?.excludedListings?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <div className="flex items-center justify-between mb-1">
                        <Typography variant="small" color="gray" className="text-xs">
                          Excluded from analysis ({listingsSummary.totalExcluded}):
                        </Typography>
                        {listingsSummary.exclusionsByReason && (
                          <Typography variant="small" color="gray" className="text-xs">
                            {Object.entries(listingsSummary.exclusionsByReason)
                              .map(([reason, count]) => `${count} ${EXCLUSION_REASON_LABELS[reason] || reason}`)
                              .join(', ')}
                          </Typography>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {listingsSummary.excludedListings.map((listing) => {
                          const isUserExcluded = listing.reason === 'user_excluded';
                          return (
                            <span
                              key={listing.id || listing.reg}
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${
                                isUserExcluded ? 'bg-amber-50 border border-amber-200' : 'bg-gray-100'
                              }`}
                              title={`${listing.year} | ${listing.totalTime}hrs | $${listing.price_usd?.toLocaleString() || 'N/A'}\n${listing.details || EXCLUSION_REASON_LABELS[listing.reason] || listing.reason}`}
                            >
                              {listing.reg}
                              {isUserExcluded && (
                                <button
                                  onClick={() => onRestoreExclusion?.(listing.reg)}
                                  disabled={isApplyingExclusions}
                                  className="text-amber-600 hover:text-red-500 disabled:opacity-50 font-bold"
                                  title="Restore to analysis"
                                >
                                  ×
                                </button>
                              )}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}

    </div>
  );
}

export default MarketTrendsSection;
